import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { completeAuth, popupDoneMessage, SpotifyAuthError } from './spotifyAuth';

interface Props {}

/**
 * Where Spotify sends the user back after authorising.
 *
 * This page is the only place the authorisation code is redeemed, on both
 * paths - the same arrangement as dropboxRedirect.tsx, and for the same
 * reason. It used to read a token out of the URL fragment and post it to the
 * opener, which meant the iOS path, where there is no opener, had nowhere to
 * put the result and simply gave up. Redeeming it here works the same whether
 * "here" is a popup on a desktop or the app itself mid-navigation on a phone,
 * and the two paths differ only in what happens afterwards:
 *
 *   popup    - tell the opener we are done and close. The opener re-reads the
 *              token store, which we have already written.
 *   redirect - navigate back into the app. /spotify-redirect renders outside
 *              the service tree (see App.tsx), so SpotifyService mounts fresh
 *              on arrival and reads the token store on mount.
 */
export default function SpotifyRedirect({}: Props) {
  const navigate = useNavigate();
  const [error, setError] = useState<string>();
  const [returnTo, setReturnTo] = useState('/app');

  // The work is a one-shot side effect. A re-run would find the code already
  // redeemed - they are single-use - and report a spurious failure.
  const handled = useRef(false);

  useEffect(
    function completeSpotifySignIn() {
      if (handled.current) {
        return;
      }

      handled.current = true;

      // Reading `opener` across origins can throw rather than return null.
      let opener: Window | null = null;

      try {
        opener = window.opener;
      } catch {
        opener = null;
      }

      completeAuth(window.location.search).then(
        function handOff(result) {
          if (opener) {
            try {
              opener.postMessage({ [popupDoneMessage]: true }, window.location.origin);
              window.close();
              return;
            } catch {
              // Fall through to navigating: the token is stored either way, so
              // the sign-in did succeed even if we cannot say so through the
              // opener.
            }
          }

          // replace, not push: the URL still carries a spent authorisation code,
          // and there is no reason to let Back land on a callback that can no
          // longer be completed.
          navigate(result.returnTo, { replace: true });
        },
        function reportFailure(err: SpotifyAuthError) {
          setReturnTo(err.returnTo || '/app');
          setError(err.message || 'Spotify sign-in failed.');
        },
      );
    },
    [navigate],
  );

  if (!error) {
    return <p>Completing Spotify sign-in…</p>;
  }

  return (
    <article className="mainViewport">
      <section>
        <h5>Spotify sign-in did not complete</h5>
        <p>{error}</p>
        <button className="button" onClick={() => navigate(returnTo, { replace: true })}>
          Back to Fadila
        </button>
      </section>
    </article>
  );
}
